import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import IncidentCard from '../Incidents/IncidentCard';
import IncidentListRow from '../Incidents/components/IncidentListRow';
import IncidentErrorBoundary from '../Incidents/components/IncidentErrorBoundary';
import ExpandedContent from '../Incidents/components/ExpandedContent';
import { useIncidentDetails } from '../Incidents/hooks/useIncidentDetails';
import {
  trackPastIncidentsSectionView,
  trackPastIncidentsLoadMore,
} from '../../../utils/modernUITracking';
import styles from './PastIncidentsSection.module.css';

/**
 * PastIncidentsSection - Container for resolved/past incident display.
 * US1937851: Browse Resolved Incidents with infinite scroll pagination.
 * US1975599: Inline expand preview on the desktop list row (chevron toggle).
 *
 * Features:
 * - Responsive: mobile cards / desktop list rows
 * - IntersectionObserver sentinel for infinite scroll
 * - Loading and error states matching IncidentSection patterns
 * - Desktop: single-expand inline preview powered by useIncidentDetails
 *   (polling disabled — past incidents are immutable)
 * - Mobile: cards are expandable with the same inline preview
 */

const PastIncidentRowComponent = ({ problem, isExpanded, onToggle, newrelic = null }) => {
  // Bind this row's problem.number to the parent's stable toggle handler.
  // Both deps are stable: onToggle is useCallback([]) from parent; problem.number is a fixed key.
  const handleToggle = useCallback(() => {
    onToggle(problem.number);
  }, [onToggle, problem.number]);

  const {
    data: incidentData,
    loading: incidentLoading,
    refreshing: incidentRefreshing,
    lastUpdated: incidentLastUpdated,
    error: incidentError,
    // fetchTimeline=false: the past-incident preview does not render the
    // consolidated timeline (ExpandedContent gates it behind !isPastIncidents),
    // so skip fetching it. Problem details (status/brand/etc.) are still fetched.
  } = useIncidentDetails(problem.number, isExpanded, false, false);

  return (
    <div>
      <IncidentListRow
        number={problem.number}
        priority={problem.priority}
        shortDescription={problem.shortDescription || 'No description available'}
        status={problem.status}
        slackChannel={problem.slackChannel}
        incidentStart={problem.incidentStart}
        incidentEnd={problem.incidentEnd}
        affectedBrand={problem.affectedBrand}
        execSummaryUrl={problem.execSummaryUrl}
        isExpanded={isExpanded}
        onToggle={handleToggle}
        section="past_incidents"
        newrelic={newrelic}
      />
      {isExpanded && (
        <div className={styles.expandedWrapper}>
          <IncidentErrorBoundary incidentNumber={problem.number}>
            <ExpandedContent
              incidentNumber={problem.number}
              incidentTitle={problem.shortDescription || 'No description available'}
              data={incidentData}
              loading={incidentLoading}
              refreshing={incidentRefreshing}
              lastUpdated={incidentLastUpdated}
              error={incidentError}
              section="past_incidents"
              newrelic={newrelic}
            />
          </IncidentErrorBoundary>
        </div>
      )}
    </div>
  );
};

PastIncidentRowComponent.propTypes = {
  problem: PropTypes.shape({
    number: PropTypes.string,
    priority: PropTypes.string,
    shortDescription: PropTypes.string,
    status: PropTypes.string,
    slackChannel: PropTypes.string,
    incidentStart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    incidentEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    affectedBrand: PropTypes.string,
    execSummaryUrl: PropTypes.string,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  newrelic: PropTypes.object,
};

const PastIncidentRow = memo(PastIncidentRowComponent);
PastIncidentRow.displayName = 'PastIncidentRow';

const PastIncidentsSection = ({
  problems = [],
  loading = false,
  loadingMore = false,
  error = null,
  hasMore = false,
  loadMore = null,
  handleRetry = null,
  newrelic = null,
  showTitle = true,
}) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );

  const [expandedRowNumbers, setExpandedRowNumbers] = useState(new Set());
  const [expandedCardIds, setExpandedCardIds] = useState(new Set());

  // Expand/Collapse + viewDurationMs are tracked by IncidentListRow/IncidentCard
  // via the unified ModernUI.Incident.Expand/Collapse events (with
  // section='past_incidents'). The wrapper just owns toggle state.
  const handleRowToggle = useCallback((problemNumber) => {
    setExpandedRowNumbers((prev) => {
      const next = new Set(prev);
      next.has(problemNumber) ? next.delete(problemNumber) : next.add(problemNumber);
      return next;
    });
  }, []);

  const handleCardToggle = useCallback((cardId) => {
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      next.has(cardId) ? next.delete(cardId) : next.add(cardId);
      return next;
    });
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // NR tracking: fire ModernUI.PastIncidents.View ONCE when data first loads.
  // `hasFiredView` is the guard that prevents re-fires — `problems.length`
  // stays in deps so the effect re-evaluates as the list grows (and reads
  // the latest count via closure), but the guard ensures we only emit once
  // per mount. The dedicated ModernUI.PastIncidents.LoadMore event covers
  // pagination separately.
  const hasFiredView = useRef(false);
  useEffect(() => {
    if (!loading && !hasFiredView.current) {
      hasFiredView.current = true;
      trackPastIncidentsSectionView(problems.length, {}, newrelic);
    }
  }, [loading, problems.length, newrelic]);

  // IntersectionObserver for infinite scroll sentinel
  const sentinelRef = useRef(null);

  const handleLoadMore = useCallback(() => {
    if (loadMore) {
      trackPastIncidentsLoadMore({}, newrelic);
      loadMore();
    }
  }, [loadMore, newrelic]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loadingMore) {
          handleLoadMore();
        }
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, handleLoadMore]);

  // Loading state (initial)
  if (loading) {
    return (
      <section className={styles.section}>
        {showTitle && <h2 className={`tw-text-2xl tw-font-bold ${styles.titleText}`}>Past Incidents</h2>}
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading past incidents...</p>
        </div>
      </section>
    );
  }

  // Error state (initial load only — no data loaded yet)
  if (error && (!problems || problems.length === 0)) {
    return (
      <section className={styles.section}>
        {showTitle && <h2 className={`tw-text-2xl tw-font-bold ${styles.titleText}`}>Past Incidents</h2>}
        <div className="tw-bg-red-50 tw-border-l-4 tw-border-red-500 tw-p-6 tw-rounded-lg tw-shadow-sm">
          <div className="tw-flex tw-items-start">
            <svg
              className="tw-w-6 tw-h-6 tw-text-red-500 tw-mr-3 tw-flex-shrink-0 tw-mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="tw-flex-1">
              <h3 className="tw-text-red-800 tw-font-semibold tw-mb-2">
                Failed to load past incidents
              </h3>
              <p className="tw-text-red-700 tw-text-sm">
                {error.message || 'An unexpected error occurred. Please try again.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state — only when there are genuinely no more pages. US1997496: a page can be
  // empty after the backend filters out temporary (PRBT) problems while more pages remain;
  // in that case fall through so the sentinel/loading indicator renders and loadMore fires
  // (see the auto-advance guard in useHistoricalProblems) instead of flashing a false empty.
  if ((!problems || problems.length === 0) && !hasMore && !loadingMore) {
    return (
      <section className={styles.section}>
        {showTitle && <h2 className={`tw-text-2xl tw-font-bold ${styles.titleText}`}>Past Incidents</h2>}
        <p className={styles.emptyText} data-testid="past-incidents-empty-state">No past incidents found.</p>
      </section>
    );
  }

  // Content state
  return (
    <section className={styles.section}>
      {showTitle && <h2 className={`tw-text-2xl tw-font-bold ${styles.titleText}`}>Past Incidents</h2>}

      {/* Mobile View: Card Grid */}
      {isMobile ? (
        <div className={styles.grid}>
          {problems.map((problem) => {
            return (
              <IncidentErrorBoundary
                key={problem.number}
                incidentNumber={problem.number}
              >
                <IncidentCard
                  number={problem.number}
                  priority={problem.priority}
                  shortDescription={problem.shortDescription || 'No description available'}
                  slackChannel={problem.slackChannel}
                  incidentStart={problem.incidentStart}
                  incidentEnd={problem.incidentEnd}
                  affectedBrand={problem.affectedBrand}
                  execSummaryUrl={problem.execSummaryUrl}
                  isExpanded={expandedCardIds.has(problem.number)}
                  onToggleExpand={() => handleCardToggle(problem.number)}
                  enablePolling={false}
                  section="past_incidents"
                  newrelic={newrelic}
                />
              </IncidentErrorBoundary>
            );
          })}
        </div>
      ) : (
        /* Desktop View: List rows, no accordion */
        <div className={styles.listContainer}>
          {problems.map((problem) => {
            if (!problem.number) return null;

            return (
              <IncidentErrorBoundary
                key={problem.number}
                incidentNumber={problem.number}
              >
                <PastIncidentRow
                  problem={problem}
                  isExpanded={expandedRowNumbers.has(problem.number)}
                  onToggle={handleRowToggle}
                  newrelic={newrelic}
                />
              </IncidentErrorBoundary>
            );
          })}
        </div>
      )}

      {/* Inline pagination error with retry */}
      {error && problems.length > 0 && (
        <div className={styles.paginationError}>
          <p className={styles.paginationErrorText}>
            Failed to load more incidents.
          </p>
          <button
            className={styles.retryButton}
            onClick={handleRetry || handleLoadMore}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className={styles.sentinel}></div>

      {/* Bottom loading spinner for "load more" */}
      {loadingMore && (
        <div className={styles.loadMoreContainer}>
          <div className={styles.spinner}></div>
        </div>
      )}
    </section>
  );
};

PastIncidentsSection.propTypes = {
  problems: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  error: PropTypes.object,
  hasMore: PropTypes.bool,
  loadMore: PropTypes.func,
  handleRetry: PropTypes.func,
  newrelic: PropTypes.object,
  showTitle: PropTypes.bool,
};

export default PastIncidentsSection;
